import type { SupabaseClient } from "@supabase/supabase-js";

import {
  clearSelectedAccount,
  fetchMyAccounts,
  persistSelectedAccount,
  readSelectedAccount,
  type MyAccount,
} from "../data/accounts.ts";
import { isStoredSelectionStale } from "../../../../packages/shared/src/account-context.ts";

export type GuardWebAccountGate =
  | {
      readonly accounts: readonly MyAccount[];
      readonly activeAccount: null;
      readonly activeAccountId: null;
      readonly message: string;
      readonly status: "loading";
    }
  | {
      readonly accounts: readonly MyAccount[];
      readonly activeAccount: null;
      readonly activeAccountId: null;
      readonly message: string;
      readonly reason: "no-memberships";
      readonly status: "blocked";
    }
  | {
      readonly accounts: readonly MyAccount[];
      readonly activeAccount: null;
      readonly activeAccountId: null;
      readonly message: string;
      readonly reason: "load-failed";
      readonly status: "error";
    }
  | {
      readonly accounts: readonly MyAccount[];
      readonly activeAccount: null;
      readonly activeAccountId: null;
      readonly message: string;
      readonly status: "picker";
      readonly suggestedAccountId: string;
    }
  | {
      readonly accounts: readonly MyAccount[];
      readonly activeAccount: MyAccount;
      readonly activeAccountId: string;
      readonly message: string;
      readonly pickerRequired: boolean;
      readonly status: "ready";
    };

export function createLoadingGuardWebAccountGate(): GuardWebAccountGate {
  return {
    accounts: [],
    activeAccount: null,
    activeAccountId: null,
    message: "Loading your workspaces…",
    status: "loading",
  };
}

export function resolveGuardWebAccountGate(input: {
  readonly accounts: readonly MyAccount[];
  readonly configAccountId: string;
  readonly storedSelection: string | null;
}): GuardWebAccountGate {
  const accounts = input.accounts;
  const storedSelection = normalizeSelection(input.storedSelection);
  const configAccountId = normalizeSelection(input.configAccountId) ?? "";

  if (accounts.length === 0) {
    return {
      accounts,
      activeAccount: null,
      activeAccountId: null,
      message: "No active workspace membership was found for this signed-in user. Ask your supervisor to add you.",
      reason: "no-memberships",
      status: "blocked",
    };
  }

  if (accounts.length === 1) {
    const activeAccount = accounts[0]!;
    return {
      accounts,
      activeAccount,
      activeAccountId: activeAccount.accountId,
      message: `${activeAccount.accountName} selected.`,
      pickerRequired: false,
      status: "ready",
    };
  }

  if (storedSelection !== null) {
    const activeAccount = accounts.find((account) => account.accountId === storedSelection);
    if (activeAccount) {
      return {
        accounts,
        activeAccount,
        activeAccountId: activeAccount.accountId,
        message: `${activeAccount.accountName} selected.`,
        pickerRequired: true,
        status: "ready",
      };
    }
  }

  return {
    accounts,
    activeAccount: null,
    activeAccountId: null,
    message: "Choose the workspace for this patrol session.",
    status: "picker",
    suggestedAccountId: pickSuggestedAccountId(configAccountId, accounts),
  };
}

export async function loadGuardWebAccountGate(client: SupabaseClient, configAccountId: string): Promise<GuardWebAccountGate> {
  const storedSelection = readSelectedAccount();
  let accounts: MyAccount[];

  try {
    accounts = await fetchMyAccounts(client);
  } catch {
    return {
      accounts: [],
      activeAccount: null,
      activeAccountId: null,
      message: "Could not verify your workspaces. Check your connection and try again.",
      reason: "load-failed",
      status: "error",
    };
  }

  if (storedSelection !== null && (accounts.length === 0 || isStoredSelectionStale(accounts, storedSelection))) {
    clearSelectedAccount();
  }

  const gate = resolveGuardWebAccountGate({
    accounts,
    configAccountId,
    storedSelection: readSelectedAccount(),
  });

  if (gate.status === "ready" && accounts.length === 1) {
    persistSelectedAccount(gate.activeAccountId);
  }

  return gate;
}

export function selectGuardWebAccount(accountId: string, accounts: readonly MyAccount[], configAccountId: string): GuardWebAccountGate {
  const normalized = normalizeSelection(accountId);
  if (normalized === null || !accounts.some((account) => account.accountId === normalized)) {
    throw new Error("Choose an available workspace.");
  }
  persistSelectedAccount(normalized);
  return resolveGuardWebAccountGate({
    accounts,
    configAccountId,
    storedSelection: normalized,
  });
}

function pickSuggestedAccountId(configAccountId: string, accounts: readonly MyAccount[]): string {
  if (configAccountId !== "" && accounts.some((account) => account.accountId === configAccountId)) {
    return configAccountId;
  }
  return accounts[0]?.accountId ?? "";
}

function normalizeSelection(value: string | null | undefined): string | null {
  const trimmed = value?.trim() ?? "";
  return trimmed.length > 0 ? trimmed : null;
}
